import { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { useMode } from '@/providers/ModeProvider';
import { useRegisterPageRefresh } from '@/contexts/PageRefreshContext';
import { dataService, isDemoMode } from '@/services/data-service';
import type { Asset, AssetStatus, AssetCategory } from '@/types/asset';
import { ASSET_STATUS_LABELS } from '@/types/asset';
import { AssetGallery } from '@/components/assets/asset-gallery';
// AssetGallerySkeleton removed - unused
import { DisposalWizard } from '@/components/assets/disposal-wizard';
import { LabelPrintingModal } from '@/components/assets/label-printing-modal';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Plus, Search, LayoutGrid, List, ArrowUpDown, ArrowUp, ArrowDown, Grid3x3, Trash2, Download, FileSpreadsheet, TrendingDown, History, X, FileText, Printer, PackageOpen, Filter, Columns, Zap, ChevronLeft, ChevronRight, ArrowRight } from 'lucide-react';
import { ProductTour } from '@/components/ui/product-tour';
import { useAssetsTour } from '@/hooks/use-assets-tour';
import { useTour } from '@/contexts/TourContext';
// Package, Settings2 removed - unused
import { exportToCSV, exportToExcel, generateFilename, formatDateForExport, formatCurrencyForExport } from '@/lib/export-utils';
import type { ExportColumn } from '@/lib/export-utils';
import { toast } from 'sonner';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { format } from 'date-fns';
import { useRightPanel } from '@/contexts/RightPanelContext';
import { PageWithRightPanel } from '@/components/layout/PageWithRightPanel';
import { RightPanelBase, type RightPanelTab } from '@/components/RightPanel/RightPanelBase';
import { AssetFiltersTab } from '@/components/RightPanel/AssetFiltersTab';
import { AssetColumnsTab } from '@/components/RightPanel/AssetColumnsTab';
import { DEFAULT_ASSET_COLUMNS } from '@/lib/asset-columns-config';
import { cn } from '@/lib/utils';
import { useColumnResize } from '@/hooks/use-column-resize';
import { LiveModePlaceholder, GallerySkeleton, TableSkeleton } from '@/components/shared/live-mode-placeholder';
import { useFeatureSettings } from '@/hooks/use-feature-settings';

type SortOption= 'category-desc' | 'category-asc' | 'name-asc' | 'name-desc' | 'date-desc' | 'date-asc' | 'value-desc' | 'value-asc';
type GridSize = 4 | 5 | 6;

// Helper function to create default pending filters object - extracted to reduce cognitive complexity
function createDefaultPendingFilters(
  categoryParam: string | null,
  statusParam: string | null,
  locationParam: string | null
) {
  return {
    selectedCategories: categoryParam ? categoryParam.split(',') : [],
    selectedStatuses: statusParam ? statusParam.split(',') : [],
    selectedLocations: locationParam ? locationParam.split(',').map(decodeURIComponent) : [],
    acquisitionDateRange: { from: undefined, to: undefined },
    acquisitionCostMin: '',
    acquisitionCostMax: '',
    netBookValueMin: '',
    netBookValueMax: '',
    selectedAssignedTo: [],
    selectedDepreciationMethods: [],
    warrantyExpiryDateRange: { from: undefined, to: undefined },
    selectedManufacturers: [],
  };
}

// Helper function to build default filters from URL params - extracted to reduce cognitive complexity
function buildDefaultFiltersFromParams(
  statusParam: string | null,
  locationParam: string | null,
  categoryParam: string | null,
  hasNoFilters: boolean
): Record<string, unknown> {
  const filters: Record<string, unknown> = {};
  
  if (statusParam) {
    filters.status = statusParam.split(',') as AssetStatus[];
  }
  // Don't apply default status filter - show all assets by default
  
  if (locationParam) {
    filters.location = locationParam.split(',');
  }
  
  if (categoryParam) {
    filters.category = categoryParam.split(',') as AssetCategory[];
  }
  
  return filters;
}

// Helper function to check if asset matches array filter - extracted to reduce cognitive complexity
function matchesArrayFilter<T>(filterValues: T[] | undefined, assetValue: T | undefined | null, defaultValue: T): boolean {
  if (!filterValues || filterValues.length === 0) return true;
  return filterValues.includes(assetValue ?? defaultValue as T);
}

// Helper function to check if asset matches date range filter - extracted to reduce cognitive complexity
function matchesDateRangeFilter(
  dateRange: { from?: Date; to?: Date } | undefined,
  assetDateStr: string | undefined
): boolean {
  if (!dateRange) return true;
  const assetDate = assetDateStr ? new Date(assetDateStr) : null;
  if (assetDate) {
    if (dateRange.from && assetDate < dateRange.from) return false;
    if (dateRange.to && assetDate > dateRange.to) return false;
  } else if (dateRange.from || dateRange.to) {
    return false;
  }
  return true;
}

// Helper function to check if asset matches numeric range filter - extracted to reduce cognitive complexity
function matchesNumericRangeFilter(
  minValue: string | undefined,
  maxValue: string | undefined,
  assetValue: number | undefined | null
): boolean {
  if (!minValue && !maxValue) return true;
  const value = assetValue ?? 0;
  if (minValue && value < parseFloat(minValue)) return false;
  if (maxValue && value > parseFloat(maxValue)) return false;
  return true;
}

// Helper function to cycle through sort states - extracted to reduce nesting depth
function getNextSortState(
  currentSortBy: SortOption | '',
  field: string,
  ascKey: SortOption,
  descKey: SortOption
): SortOption | '' {
  if (currentSortBy === '' || !currentSortBy.includes(field)) return ascKey;
  if (currentSortBy === ascKey) return descKey;
  return '';
}

// Helper function to build pending filters from URL params - extracted to reduce cognitive complexity
function buildPendingFiltersFromUrlParams(
  statusParam: string | null,
  locationParam: string | null,
  categoryParam: string | null
) {
  return {
    selectedCategories: categoryParam ? categoryParam.split(',').map(decodeURIComponent) : [],
    selectedStatuses: statusParam ? statusParam.split(',') : [],
    selectedLocations: locationParam ? locationParam.split(',').map(decodeURIComponent) : [],
    selectedSubCategories: [],
    selectedSubLocations: [],
    acquisitionDateRange: { from: undefined, to: undefined },
    acquisitionCostMin: '',
    acquisitionCostMax: '',
    netBookValueMin: '',
    netBookValueMax: '',
    selectedAssignedTo: [],
    selectedDepreciationMethods: [],
    warrantyExpiryDateRange: { from: undefined, to: undefined },
    selectedManufacturers: [],
  };
}

// Helper function to handle URL filter params - extracted to reduce cognitive complexity
function handleUrlFilterParams(
  statusParam: string | null,
  locationParam: string | null,
  categoryParam: string | null,
  existingFilters: Record<string, unknown>
): Record<string, unknown> | null {
  if (!statusParam && !locationParam && !categoryParam) {
    return null;
  }
  return {
    ...existingFilters,
    ...(statusParam && { status: statusParam.split(',') as AssetStatus[] }),
    ...(locationParam && { location: locationParam.split(',').map(decodeURIComponent) }),
    ...(categoryParam && { category: categoryParam.split(',').map(decodeURIComponent) as AssetCategory[] }),
  };
}

export function AssetsPage(): React.ReactElement {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { mode } = useMode();
  useFeatureSettings(); // Hook called for side effects
  const [assets, setAssets] = useState<Asset[]>([]);
  
  // Product Tour
  const { isTourOpen, tourSteps, startTour, closeTour, completeTour } = useAssetsTour();
  const { setOnStartTour } = useTour();
  
  // Register tour with context
  useEffect(() => {
    setOnStartTour(() => startTour);
    return () => setOnStartTour(null);
  }, [setOnStartTour, startTour]);
  
  const [loading, setLoading] = useState(true);
  const [initialLoad, setInitialLoad] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'gallery' | 'list'>(() => {
    // Load persisted view mode from localStorage, default to 'gallery'
    const savedViewMode = localStorage.getItem('assetViewMode');
    return (savedViewMode === 'list' || savedViewMode === 'gallery') ? savedViewMode : 'gallery';
  });
  const [gridSize, setGridSize] = useState<GridSize>(5);
  const [searchQuery, setSearchQuery] = useState('');
  const [importJobInfo, setImportJobInfo] = useState<{ jobId: string; fileName: string } | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [disposalAsset, setDisposalAsset] = useState<Asset | null>(null);
  const [showLabelPrintingModal, setShowLabelPrintingModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 100;

  // Right Panel Context
  const {
    setIsExpanded,
    setActiveTab,
    selectedRowIds,
    setSelectedRowIds,
    visibleColumns: contextVisibleColumns,
    setVisibleColumns,
    columnOrder: contextColumnOrder,
    setColumnOrder,
    tableFilters,
    setTableFilters,
    tableSorting,
    setTableSorting,
    isPinned,
    activeFilterCount,
    setDefaultFilters,
    isApplyingFilters,
    setPendingFilters,
  } = useRightPanel();
  
  // Get sortBy from context (can be empty string or SortOption)
  const sortBy = (tableSorting.sortBy as SortOption | '') ?? '';
  
  // Helper function to update sorting in context
  const setSortBy = (value: SortOption | '') => {
    setTableSorting({ sortBy: value });
  };

  // Column resize hook
  const initialColumnWidths = {
    select: 80,
    assetCode: 150,
    assetName: 250,
    description: 300,
    category: 180,
    location: 200,
    status: 120,
    serialNumber: 180,
    barcode: 150,
    qrCode: 120,
    acquisitionCost: 150,
    netBookValue: 150,
    depreciationRate: 150,
    acquisitionDate: 150,
    disposalDate: 150,
    warrantyExpiry: 150,
    assignedTo: 180,
    department: 150,
    supplier: 180,
    purchaseOrderNumber: 180,
    notes: 250
  };

  const { columnWidths, handleMouseDown } = useColumnResize(initialColumnWidths, ['select']);

  // Initialize visible columns from context or defaults
  const [isInitialized, setIsInitialized] = useState(false);
  
  useEffect(() => {
    if (!isInitialized) {
      // Always reset columns for Assets page
      const defaultVisible = DEFAULT_ASSET_COLUMNS
        .filter(col => col.visible)
        .map(col => col.id);
      setVisibleColumns(defaultVisible);
      setColumnOrder(DEFAULT_ASSET_COLUMNS.map(col => col.id));
      
      setIsInitialized(true);
    }
  }, [isInitialized, searchParams]);

  // Apply default filter and sorting only on initial load when no URL params and no saved state
  const hasAppliedDefaults = useRef(false);
  
  useEffect(() => {
    if (!isInitialized || hasAppliedDefaults.current) return;
    
    const statusParam = searchParams.get('status');
    const locationParam = searchParams.get('location');
    const categoryParam = searchParams.get('category');
    const filterParam = searchParams.get('filter');
    const importJobIdParam = searchParams.get('importJobId');
    
    const hasNoFilters = !tableFilters.status && !tableFilters.category && !tableFilters.location;
    const hasNoSorting = !tableSorting.sortBy;
    const hasUrlParams = statusParam || locationParam || categoryParam || filterParam || importJobIdParam;
    
    const defaultFiltersFromParams = buildDefaultFiltersFromParams(statusParam, locationParam, categoryParam, hasNoFilters);
    setDefaultFilters(defaultFiltersFromParams);
    
    if (!hasUrlParams) {
      // Apply default status filter to show active assets
      if (hasNoFilters) {
        const defaultStatuses: AssetStatus[] = ['inService', 'pending', 'reserved', 'underMaintenance'];
        setTableFilters({ status: defaultStatuses });
        setPendingFilters({
          selectedCategories: [],
          selectedStatuses: defaultStatuses,
          selectedLocations: [],
          selectedSubCategories: [],
          selectedSubLocations: [],
          acquisitionDateRange: { from: undefined, to: undefined },
          acquisitionCostMin: '',
          acquisitionCostMax: '',
          netBookValueMin: '',
          netBookValueMax: '',
          selectedAssignedTo: [],
          selectedDepreciationMethods: [],
          warrantyExpiryDateRange: { from: undefined, to: undefined },
          selectedManufacturers: [],
        });
      }
      if (hasNoSorting) {
        setTableSorting({ sortBy: 'category-asc' });
      }
    } else if (Object.keys(defaultFiltersFromParams).length > 0) {
      setTableFilters(defaultFiltersFromParams);
      // Update pending filters to show URL params in the filter UI
      setPendingFilters({
        selectedCategories: categoryParam ? categoryParam.split(',').map(decodeURIComponent) : [],
        selectedStatuses: statusParam ? statusParam.split(',') : [],
        selectedLocations: locationParam ? locationParam.split(',').map(decodeURIComponent) : [],
        selectedSubCategories: [],
        selectedSubLocations: [],
        acquisitionDateRange: { from: undefined, to: undefined },
        acquisitionCostMin: '',
        acquisitionCostMax: '',
        netBookValueMin: '',
        netBookValueMax: '',
        selectedAssignedTo: [],
        selectedDepreciationMethods: [],
        warrantyExpiryDateRange: { from: undefined, to: undefined },
        selectedManufacturers: [],
      });
    }
    
    hasAppliedDefaults.current = true;
  }, [isInitialized, searchParams, tableFilters, tableSorting, setDefaultFilters, setPendingFilters]);

  // Persist view mode to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('assetViewMode', viewMode);
  }, [viewMode]);

  // Use context columns or fall back to defaults
  const visibleColumns = contextVisibleColumns.length > 0 
    ? contextVisibleColumns 
    : DEFAULT_ASSET_COLUMNS.filter(col => col.visible).map(col => col.id);

  const columnOrder = contextColumnOrder.length > 0
    ? contextColumnOrder
    : DEFAULT_ASSET_COLUMNS.map(col => col.id);

  // Build ordered visible columns for rendering
  const orderedVisibleColumns = columnOrder
    .filter(id => visibleColumns.includes(id))
    .map(id => DEFAULT_ASSET_COLUMNS.find(col => col.id === id))
    .filter(Boolean) as typeof DEFAULT_ASSET_COLUMNS;

  // Reset search query and page on mount (unless deep-linking with URL params)
  useEffect(() => {
    const statusParam = searchParams.get('status');
    const locationParam = searchParams.get('location');
    const categoryParam = searchParams.get('category');
    const filterParam = searchParams.get('filter');
    const importJobIdParam = searchParams.get('importJobId');
    
    if (!statusParam && !locationParam && !categoryParam && !filterParam && !importJobIdParam) {
      // No deep-link params, reset search query and page (but keep default filters)
      setSearchQuery('');
      setCurrentPage(1);
    }
  }, []); // Only run on mount

  useEffect(() => {
    const statusParam = searchParams.get('status');
    const locationParam = searchParams.get('location');
    const categoryParam = searchParams.get('category');
    const idParam = searchParams.get('id');
    const importJobIdParam = searchParams.get('importJobId');
    
    // Handle URL filter params using helper function
    const newFilters = handleUrlFilterParams(statusParam, locationParam, categoryParam, tableFilters);
    if (newFilters) {
      setTableFilters(newFilters);
      setPendingFilters(buildPendingFiltersFromUrlParams(statusParam, locationParam, categoryParam));
    }

    // Handle import job ID filter
    const fileNameParam = importJobIdParam ? searchParams.get('fileName') : null;
    setImportJobInfo(importJobIdParam ? { jobId: importJobIdParam, fileName: fileNameParam ?? 'Unknown Import' } : null);

    // Handle direct asset navigation via id parameter
    if (idParam && assets.length > 0) {
      const asset = assets.find((a) => a.id === idParam);
      if (asset) {
        navigate(`/assets/${asset.id}`, { replace: true });
      }
    }
  }, [searchParams]);

  // Extract loadAssets as a stable function for refresh
  const loadAssets = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setAssets([]);
      
      let assetList: Asset[] = [];
      
      if (mode === 'demo') {
        const data = dataService.getDemoAssets();
        assetList = Array.isArray(data) ? data : (data as { data: Asset[] })?.data ?? [];
      } else {
        // In live mode, only fetch from API - no demo data fallback
        try {
          const data = await dataService.getAssetsApiOnly();
          assetList = Array.isArray(data) ? data : (data as { data: Asset[] })?.data ?? [];
        } catch (apiError) {
          console.error('API call failed in live mode:', apiError);
          setError('Failed to load assets from API. Please check your connection.');
          // Don't fall back to demo data in live mode
          assetList = [];
        }
      }
      
      setAssets(assetList);
    } catch (err) {
      console.error('Failed to load assets:', err);
      setError('Failed to load assets. Please try again.');
    } finally {
      setLoading(false);
      setInitialLoad(false);
    }
  }, [mode]);

  useEffect(() => {
    loadAssets();
  }, [loadAssets]);

  // Register refresh handler for breadcrumb
  useRegisterPageRefresh(loadAssets, location.pathname);

  const isDateWithinRange = (dateStr: string | undefined, today: Date, thirtyDaysFromNow: Date): boolean => {
    if (!dateStr) return false;
    const date = new Date(dateStr);
    return date <= thirtyDaysFromNow && date >= today;
  };

  const matchesRequiringAttentionFilter = (asset: Asset): boolean => {
    const today = new Date();
    const thirtyDaysFromNow = new Date(today);
    thirtyDaysFromNow.setDate(today.getDate() + 30);

    return (
      isDateWithinRange(asset.warrantyExpiryDate, today, thirtyDaysFromNow) ||
      isDateWithinRange(asset.insuranceExpiryDate, today, thirtyDaysFromNow) ||
      isDateWithinRange(asset.userDate1, today, thirtyDaysFromNow) ||
      asset.status === 'disposed'
    );
  };

  const matchesSearchQuery = (asset: Asset, query: string): boolean => {
    const lowerQuery = query.toLowerCase();
    return (
      asset.name.toLowerCase().includes(lowerQuery) ||
      asset.code.toLowerCase().includes(lowerQuery) ||
      (asset.serialNumber?.toLowerCase().includes(lowerQuery) ?? false) ||
      (asset.description?.toLowerCase().includes(lowerQuery) ?? false)
    );
  };

  const filteredAssets = assets
    .filter((asset) => {
      if (importJobInfo && asset.importJobId !== importJobInfo.jobId) return false;

      const filterParam = searchParams.get('filter');
      if (filterParam === 'requiring-attention') {
        return matchesRequiringAttentionFilter(asset);
      }

      if (searchQuery && !matchesSearchQuery(asset, searchQuery)) return false;

      // Use helper functions to check array filters
      if (!matchesArrayFilter(tableFilters.status as AssetStatus[] | undefined, asset.status, 'inService' as AssetStatus)) return false;
      if (!matchesArrayFilter(tableFilters.category as AssetCategory[] | undefined, asset.category, '' as AssetCategory)) return false;
      if (!matchesArrayFilter(tableFilters.location as string[] | undefined, asset.location, '')) return false;
      if (!matchesArrayFilter(tableFilters.department as string[] | undefined, asset.costCentre, '')) return false;
      if (!matchesArrayFilter(tableFilters.assignedTo as string[] | undefined, asset.assignedTo, '')) return false;
      if (!matchesArrayFilter(tableFilters.manufacturer as string[] | undefined, asset.manufacturer, '')) return false;
      if (!matchesArrayFilter(tableFilters.depreciationMethod as string[] | undefined, asset.depreciationMethod, '')) return false;

      // Use helper functions to check date range filters
      if (!matchesDateRangeFilter(tableFilters.acquisitionDateRange as { from?: Date; to?: Date } | undefined, asset.acquisitionDate)) return false;
      if (!matchesDateRangeFilter(tableFilters.warrantyExpiryDateRange as { from?: Date; to?: Date } | undefined, asset.warrantyExpiryDate)) return false;

      // Use helper functions to check numeric range filters
      if (!matchesNumericRangeFilter(tableFilters.acquisitionCostMin as string | undefined, tableFilters.acquisitionCostMax as string | undefined, asset.acquisitionCost)) return false;
      if (!matchesNumericRangeFilter(tableFilters.netBookValueMin as string | undefined, tableFilters.netBookValueMax as string | undefined, asset.netBookValue)) return false;

      return true;
    })
    .sort((a: any, b: any) => {
      // If sortBy is empty, maintain original order (no sorting)
      if (!sortBy) return 0;
      
      switch (sortBy) {
        case 'category-desc':
          return b.category.localeCompare(a.category);
        case 'category-asc':
          return a.category.localeCompare(b.category);
        case 'name-asc':
          return a.name.localeCompare(b.name);
        case 'name-desc':
          return b.name.localeCompare(a.name);
        case 'date-desc':
          return new Date(b.acquisitionDate).getTime() - new Date(a.acquisitionDate).getTime();
        case 'date-asc':
          return new Date(a.acquisitionDate).getTime() - new Date(b.acquisitionDate).getTime();
        case 'value-desc':
          return b.netBookValue - a.netBookValue;
        case 'value-asc':
          return a.netBookValue - b.netBookValue;
        default:
          return 0;
      }
    });

  // Pagination calculations
  const totalPages = Math.ceil(filteredAssets.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedAssets = filteredAssets.slice(startIndex, endIndex);

  // Reset to page 1 when filters or search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, tableFilters]);

  // Debug: Monitor filter changes
  useEffect(() => {
    console.log('tableFilters changed:', tableFilters);
  }, [tableFilters]);

  const handleAssetClick = (asset: Asset, mode?: 'edit' | 'view', tab?: string) => {
    const url = tab ? `/assets/${asset.id}?tab=${tab}` : `/assets/${asset.id}`;
    navigate(url);
  };

  const handleNewAsset = () => {
    navigate('/assets/new?mode=create');
  };

  // Row selection handler
  const handleSelectionChange = useCallback((assetId: string, checked: boolean) => {
    console.log('handleSelectionChange called:', { assetId, checked, currentFilters: tableFilters });
    setSelectedRowIds(prev => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(assetId);
        if (newSet.size === 1 && !isPinned) {
          setIsExpanded(true);
          setActiveTab('actions');
        }
      } else {
        newSet.delete(assetId);
        if (newSet.size === 0 && !isPinned) {
          setIsExpanded(false);
        }
      }
      console.log('New selection set:', newSet);
      return newSet;
    });
  }, [setIsExpanded, setActiveTab, setSelectedRowIds, isPinned, tableFilters]);

  // Select all handler
  const handleSelectAll = useCallback((checked: boolean) => {
    if (checked) {
      const allIds = new Set(paginatedAssets.map(asset => asset.id));
      setSelectedRowIds(allIds);
      if (allIds.size > 0 && !isPinned) {
        setIsExpanded(true);
        setActiveTab('actions');
      }
    } else {
      setSelectedRowIds(new Set());
      if (!isPinned) {
        setIsExpanded(false);
      }
    }
  }, [paginatedAssets, setSelectedRowIds, setIsExpanded, setActiveTab, isPinned]);

  const handleDelete = () => {
    setShowDeleteDialog(true);
  };

  const confirmDelete = async () => {
    try {
      if (isDemoMode()) {
        setAssets((prev) => prev.filter((a) => !selectedRowIds.has(a.id)));
        setSelectedRowIds(new Set());
        setShowDeleteDialog(false);
        toast.success(`Deleted ${selectedRowIds.size} asset(s)`);
        return;
      }

      const deletePromises = Array.from(selectedRowIds).map(id => 
        dataService.deleteAsset(id)
      );

      await Promise.all(deletePromises);

      setAssets((prev) => prev.filter((a) => !selectedRowIds.has(a.id)));
      setSelectedRowIds(new Set());
      setShowDeleteDialog(false);
      toast.success(`Successfully deleted ${selectedRowIds.size} asset(s)`);
    } catch (error: any) {
      console.error('Failed to delete assets:', error);
      setShowDeleteDialog(false);
      
      let errorMessage = 'Failed to delete assets. Please try again.';
      
      if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
    }
  };

  const handleExportCSV = () => {
    try {
      const columns: ExportColumn[] = [];
      
      orderedVisibleColumns.forEach(column => {
        switch (column.id) {
          case 'code':
            columns.push({ key: 'code', label: 'Code' });
            break;
          case 'name':
            columns.push({ key: 'name', label: 'Asset Name' });
            break;
          case 'description':
            columns.push({ key: 'description', label: 'Description' });
            break;
          case 'category':
            columns.push({ key: 'category', label: 'Category' });
            break;
          case 'location':
            columns.push({ key: 'location', label: 'Location' });
            break;
          case 'status':
            columns.push({ key: 'status', label: 'Status' });
            break;
          case 'serialNumber':
            columns.push({ key: 'serialNumber', label: 'Serial Number' });
            break;
          case 'manufacturer':
            columns.push({ key: 'manufacturer', label: 'Manufacturer' });
            break;
          case 'model':
            columns.push({ key: 'model', label: 'Model' });
            break;
          case 'acquisitionDate':
            columns.push({ key: 'acquisitionDate', label: 'Acquisition Date', format: formatDateForExport });
            break;
          case 'acquisitionCost':
            columns.push({ key: 'acquisitionCost', label: 'Acquisition Cost', format: formatCurrencyForExport });
            break;
          case 'netBookValue':
            columns.push({ key: 'netBookValue', label: 'Net Book Value', format: formatCurrencyForExport });
            break;
          case 'accumulatedDepreciation':
            columns.push({ key: 'accumulatedDepreciation', label: 'Accumulated Depreciation', format: formatCurrencyForExport });
            break;
          case 'assignedTo':
            columns.push({ key: 'assignedTo', label: 'Assigned To' });
            break;
          case 'department':
            columns.push({ key: 'department', label: 'Department' });
            break;
          case 'warrantyExpiryDate':
            columns.push({ key: 'warrantyExpiryDate', label: 'Warranty Expiry', format: formatDateForExport });
            break;
          case 'insuranceExpiryDate':
            columns.push({ key: 'insuranceExpiryDate', label: 'Insurance Expiry', format: formatDateForExport });
            break;
        }
      });

      exportToCSV({
        filename: generateFilename('assets-export', 'csv'),
        columns,
        data: filteredAssets,
      });

      toast.success(`Exported ${filteredAssets.length} asset(s) to CSV`);
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to export data');
    }
  };

  const handleExportExcel = () => {
    try {
      const columns: ExportColumn[] = [];
      
      orderedVisibleColumns.forEach(column => {
        switch (column.id) {
          case 'code':
            columns.push({ key: 'code', label: 'Code' });
            break;
          case 'name':
            columns.push({ key: 'name', label: 'Asset Name' });
            break;
          case 'description':
            columns.push({ key: 'description', label: 'Description' });
            break;
          case 'category':
            columns.push({ key: 'category', label: 'Category' });
            break;
          case 'location':
            columns.push({ key: 'location', label: 'Location' });
            break;
          case 'status':
            columns.push({ key: 'status', label: 'Status' });
            break;
          case 'serialNumber':
            columns.push({ key: 'serialNumber', label: 'Serial Number' });
            break;
          case 'manufacturer':
            columns.push({ key: 'manufacturer', label: 'Manufacturer' });
            break;
          case 'model':
            columns.push({ key: 'model', label: 'Model' });
            break;
          case 'acquisitionDate':
            columns.push({ key: 'acquisitionDate', label: 'Acquisition Date', format: formatDateForExport });
            break;
          case 'acquisitionCost':
            columns.push({ key: 'acquisitionCost', label: 'Acquisition Cost', format: formatCurrencyForExport });
            break;
          case 'netBookValue':
            columns.push({ key: 'netBookValue', label: 'Net Book Value', format: formatCurrencyForExport });
            break;
          case 'accumulatedDepreciation':
            columns.push({ key: 'accumulatedDepreciation', label: 'Accumulated Depreciation', format: formatCurrencyForExport });
            break;
          case 'assignedTo':
            columns.push({ key: 'assignedTo', label: 'Assigned To' });
            break;
          case 'department':
            columns.push({ key: 'department', label: 'Department' });
            break;
          case 'warrantyExpiryDate':
            columns.push({ key: 'warrantyExpiryDate', label: 'Warranty Expiry', format: formatDateForExport });
            break;
          case 'insuranceExpiryDate':
            columns.push({ key: 'insuranceExpiryDate', label: 'Insurance Expiry', format: formatDateForExport });
            break;
        }
      });

      exportToExcel({
        filename: generateFilename('assets-export', 'xls'),
        sheetName: 'Assets',
        columns,
        data: filteredAssets,
      });

      toast.success(`Exported ${filteredAssets.length} asset(s) to Excel`);
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to export data');
    }
  };

  const handleCreateDepreciation = () => {
    const selectedAssets = assets.filter((a) => selectedRowIds.has(a.id));
    navigate('/transactions/depreciation', { state: { selectedAssets } });
  };

  const handleViewHistory = () => {
    const assetIds = Array.from(selectedRowIds);
    navigate(`/enquiry?assetIds=${assetIds.join(',')}`);
  };

  const handleViewAudit = () => {
    const assetIds = Array.from(selectedRowIds);
    navigate(`/audit?assetId=${assetIds.join(',')}`);
  };

  const handleAddition = () => {
    const selectedAssets = assets.filter((a) => selectedRowIds.has(a.id));
    navigate('/transactions/additions', { state: { selectedAssets } });
  };

  const handleRevaluation = () => {
    const selectedAssets = assets.filter((a) => selectedRowIds.has(a.id));
    navigate('/transactions/revaluation', { state: { selectedAssets } });
  };

  const handleDisposal = () => {
    if (selectedRowIds.size === 1) {
      const assetId = Array.from(selectedRowIds)[0];
      const asset = assets.find((a) => a.id === assetId);
      if (asset) {
        // Use nullish coalescing to default to 1 if quantity is null/undefined
        // This aligns with the business rule that assets without a quantity are treated as single items
        const effectiveQuantity = asset.quantity ?? 1;
        if (effectiveQuantity <= 0) {
          toast.error('No quantity available to dispose');
          return;
        }
        setDisposalAsset(asset);
      }
    }
  };

  const handleDisposalComplete = () => {
    async function reloadAssets() {
      try {
        const data = await dataService.getAssets();
        const assetList = Array.isArray(data) ? data : (data as any)?.data ?? [];
        setAssets(assetList);
        setSelectedRowIds(new Set());
      } catch (err) {
        console.error('Failed to reload assets:', err);
      }
    }
    reloadAssets();
  };

  const handlePrintLabels = () => {
    setShowLabelPrintingModal(true);
  };

  const selectedAssets = assets.filter((a) => selectedRowIds.has(a.id));

  const formatCurrency = (value: number, currency: string = 'GBP') => {
    return `${currency} ${value.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Helper function to get sort key for a column
  const getSortKeyForColumn = (columnId: string): keyof Asset | null => {
    const sortKeyMap: Record<string, keyof Asset> = {
      code: 'code',
      name: 'name',
      category: 'category',
      location: 'location',
      status: 'status',
      serialNumber: 'serialNumber',
      acquisitionDate: 'acquisitionDate',
      acquisitionCost: 'acquisitionCost',
      netBookValue: 'netBookValue',
      accumulatedDepreciation: 'accumulatedDepreciation',
    };
    return sortKeyMap[columnId] ?? null;
  };

  // Sort icon component
  const SortIcon = ({ columnKey }: { columnKey: keyof Asset }) => {
    // If no sort is applied, show no icon
    if (!sortBy) return null;
    
    // Map current sortBy to column key
    const getCurrentSortKey = () => {
      if (sortBy.includes('name')) return 'name';
      if (sortBy.includes('category')) return 'category';
      if (sortBy.includes('date')) return 'acquisitionDate';
      if (sortBy.includes('value')) return 'netBookValue';
      return null;
    };
    const currentSortKey = getCurrentSortKey();
    
    if (currentSortKey !== columnKey) {
      return null;
    }
    
    const isAsc = sortBy.includes('asc');
    return isAsc ? <ArrowUp className="ml-2 h-4 w-4" /> : <ArrowDown className="ml-2 h-4 w-4" />;
  };

  // Helper function to render table cell content
  const renderCellContent = (columnId: string, asset: Asset) => {
    switch (columnId) {
      case 'code':
        return (
          <TableCell key={columnId} style={{ width: `${columnWidths[columnId]}px` }}>
            <button
              onClick={() => handleAssetClick(asset)}
              className="text-primary hover:text-primary/80 font-medium hover:underline underline-offset-2 transition-all cursor-pointer inline-flex items-center gap-1 group"
            >
              {asset.code}
              <span className="opacity-0 group-hover:opacity-100 transition-opacity">→</span>
            </button>
          </TableCell>
        );
      case 'name':
        return (
          <TableCell key={columnId} style={{ width: `${columnWidths[columnId]}px` }}>
            <button
              onClick={() => handleAssetClick(asset)}
              className="text-primary hover:text-primary/80 font-medium hover:underline underline-offset-2 transition-all cursor-pointer inline-flex items-center gap-1 group"
            >
              {asset.name}
              <span className="opacity-0 group-hover:opacity-100 transition-opacity">→</span>
            </button>
          </TableCell>
        );
            case 'description':
              return <TableCell key={columnId} style={{ width: `${columnWidths[columnId]}px` }} className="text-slate-600 dark:text-slate-400">{asset.description ?? '-'}</TableCell>;
              // S6606: Already using nullish coalescing
      case 'category':
        return <TableCell key={columnId} style={{ width: `${columnWidths[columnId]}px` }} className="text-slate-700 dark:text-slate-300">{asset.category}</TableCell>;
      case 'location':
        return <TableCell key={columnId} style={{ width: `${columnWidths[columnId]}px` }} className="text-slate-700 dark:text-slate-300">{asset.location}</TableCell>;
      case 'status':
        return (
          <TableCell key={columnId} style={{ width: `${columnWidths[columnId]}px` }}>
            <span className={cn(
              "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium",
              asset.status === 'inService' && 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400',
              asset.status === 'outOfService' && 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-400',
              asset.status === 'disposed' && 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-400',
              asset.status === 'fullyDepreciated' && 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
              asset.status === 'pending' && 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
              asset.status === 'underMaintenance' && 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400',
              asset.status === 'reserved' && 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-400',
              asset.status === 'lostMissing' && 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
              asset.status === 'leased' && 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-400'
            )}>
              {ASSET_STATUS_LABELS[asset.status] || asset.status}
            </span>
          </TableCell>
        );
      case 'serialNumber':
        return <TableCell key={columnId} style={{ width: `${columnWidths[columnId]}px` }} className="text-slate-700 dark:text-slate-300">{asset.serialNumber ?? '-'}</TableCell>;
      case 'manufacturer':
        return <TableCell key={columnId} style={{ width: `${columnWidths[columnId]}px` }} className="text-slate-700 dark:text-slate-300">{asset.manufacturer ?? '-'}</TableCell>;
      case 'model':
        return <TableCell key={columnId} style={{ width: `${columnWidths[columnId]}px` }} className="text-slate-700 dark:text-slate-300">{asset.model ?? '-'}</TableCell>;
      case 'acquisitionDate':
        return <TableCell key={columnId} style={{ width: `${columnWidths[columnId]}px` }} className="text-slate-700 dark:text-slate-300">{format(new Date(asset.acquisitionDate), 'dd/MM/yyyy')}</TableCell>;
      case 'acquisitionCost':
        return <TableCell key={columnId} style={{ width: `${columnWidths[columnId]}px` }} className="text-right text-slate-700 dark:text-slate-300">{formatCurrency(asset.acquisitionCost, asset.currency)}</TableCell>;
      case 'netBookValue':
        return <TableCell key={columnId} style={{ width: `${columnWidths[columnId]}px` }} className="text-right text-slate-700 dark:text-slate-300">{formatCurrency(asset.netBookValue, asset.currency)}</TableCell>;
      case 'accumulatedDepreciation':
        return <TableCell key={columnId} style={{ width: `${columnWidths[columnId]}px` }} className="text-right text-slate-700 dark:text-slate-300">{formatCurrency(asset.accumulatedDepreciation, asset.currency)}</TableCell>;
      case 'assignedTo':
        return <TableCell key={columnId} style={{ width: `${columnWidths[columnId]}px` }} className="text-slate-700 dark:text-slate-300">{asset.assignedTo ?? '-'}</TableCell>;
      case 'depreciationMethod':
        return <TableCell key={columnId} style={{ width: `${columnWidths[columnId]}px` }} className="text-slate-700 dark:text-slate-300">{asset.depreciationMethod ?? '-'}</TableCell>;
      case 'usefulLife':
        return <TableCell key={columnId} style={{ width: `${columnWidths[columnId]}px` }} className="text-slate-700 dark:text-slate-300">{asset.usefulLife ? `${asset.usefulLife} years` : '-'}</TableCell>;
      case 'warrantyExpiryDate':
        return <TableCell key={columnId} style={{ width: `${columnWidths[columnId]}px` }} className="text-slate-700 dark:text-slate-300">{asset.warrantyExpiryDate ? format(new Date(asset.warrantyExpiryDate), 'dd/MM/yyyy') : '-'}</TableCell>;
      case 'insuranceExpiryDate':
        return <TableCell key={columnId} style={{ width: `${columnWidths[columnId]}px` }} className="text-slate-700 dark:text-slate-300">{asset.insuranceExpiryDate ? format(new Date(asset.insuranceExpiryDate), 'dd/MM/yyyy') : '-'}</TableCell>;
      case 'quantity':
        return <TableCell key={columnId} style={{ width: `${columnWidths[columnId]}px` }} className="text-right text-slate-700 dark:text-slate-300">{asset.quantity ?? '-'}</TableCell>;
      case 'barcode':
        return <TableCell key={columnId} style={{ width: `${columnWidths[columnId]}px` }} className="text-slate-700 dark:text-slate-300">{asset.barcode ?? '-'}</TableCell>;
      case 'barcodeValue':
        return <TableCell key={columnId} style={{ width: `${columnWidths[columnId]}px` }} className="text-slate-700 dark:text-slate-300">{asset.barcodeValue ?? '-'}</TableCell>;
      default:
        return <TableCell key={columnId} style={{ width: `${columnWidths[columnId]}px` }} className="text-slate-700 dark:text-slate-300">-</TableCell>;
    }
  };

  // Action Card Component for better visual hierarchy
  const ActionCard = ({ icon: Icon, title, description, onClick, variant, disabled }: {
    icon: React.ElementType;
    title: string;
    description: string;
    onClick?: () => void;
    variant: 'view' | 'action' | 'delete';
    disabled?: boolean;
  }) => {
    const variantStyles = {
      view: {
        container: 'bg-blue-50 dark:bg-blue-950/30 hover:bg-blue-100 dark:hover:bg-blue-950/50',
        icon: 'text-blue-600 dark:text-blue-400',
        iconBg: 'bg-blue-100 dark:bg-blue-900/50'
      },
      action: {
        container: 'bg-primary/5 dark:bg-primary/10 hover:bg-primary/10 dark:hover:bg-primary/15',
        icon: 'text-primary dark:text-primary',
        iconBg: 'bg-primary/10 dark:bg-primary/20'
      },
      delete: {
        container: 'bg-red-50 dark:bg-red-950/30 hover:bg-red-100 dark:hover:bg-red-950/50',
        icon: 'text-red-600 dark:text-red-400',
        iconBg: 'bg-red-100 dark:bg-red-900/50'
      }
    };

    const styles = variantStyles[variant];

    return (
      <button
        onClick={onClick}
        disabled={disabled}
        className={cn(
          'w-full p-[5px] rounded-lg transition-all duration-200 text-left group cursor-pointer',
          'disabled:opacity-50 disabled:cursor-not-allowed',
          styles.container
        )}
      >
        <div className="flex items-center gap-3">
          <div className={cn(
            'flex-shrink-0 w-12 h-12 rounded-lg flex items-center justify-center transition-transform group-hover:scale-110',
            styles.iconBg
          )}>
            <Icon className={cn('h-6 w-6', styles.icon)} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2">
              <h3 className="font-semibold text-sm text-foreground">
                {title}
              </h3>
              <ArrowRight className={cn('h-4 w-4 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity', styles.icon)} />
            </div>
            <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
              {description}
            </p>
          </div>
        </div>
      </button>
    );
  };

  // Custom Actions Tab with Export functionality
  const CustomActionsTab = () => {
    const hasSelection = selectedRowIds.size > 0;
    const isSingleSelection = selectedRowIds.size === 1;

    return (
      <div className="p-4 space-y-3">
        {/* Export Section - Always visible */}
        <div className="space-y-2">
          <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider px-1">
            Export
          </h4>
          <ActionCard
            icon={Download}
            title="Export to CSV"
            description="Download all assets as CSV file"
            variant="action"
            onClick={handleExportCSV}
          />
          <ActionCard
            icon={FileSpreadsheet}
            title="Export to Excel"
            description="Generate Excel spreadsheet with all data"
            variant="action"
            onClick={handleExportExcel}
          />
        </div>

        {!hasSelection ? (
          <div className="flex flex-col items-center justify-center py-12 px-4 text-center border-t mt-4 pt-8">
            <p className="text-xs text-muted-foreground">
              Select one or more assets to see additional actions
            </p>
          </div>
        ) : (
          <>
            {/* View History */}
            <div className="space-y-2 border-t pt-3">
              <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider px-1">
                View History
              </h4>
              <ActionCard
                icon={History}
                title="Transaction History"
                description="View all transactions for selected assets"
                variant="view"
                onClick={handleViewHistory}
              />
              <ActionCard
                icon={FileText}
                title="View Audit"
                description="Open audit records and compliance data"
                variant="view"
                onClick={handleViewAudit}
              />
            </div>

            {/* Create Transactions */}
            <div className="space-y-2 border-t pt-3">
              <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider px-1">
                Create Transactions
              </h4>
              <ActionCard
                icon={Plus}
                title="Addition"
                description="Record new asset additions"
                variant="action"
                onClick={handleAddition}
              />
              <ActionCard
                icon={TrendingDown}
                title="Depreciation"
                description="Calculate and apply depreciation"
                variant="action"
                onClick={handleCreateDepreciation}
              />
              <ActionCard
                icon={TrendingDown}
                title="Revaluation"
                description="Adjust asset values and revaluate"
                variant="action"
                onClick={handleRevaluation}
              />
              <ActionCard
                icon={Trash2}
                title="Disposal"
                description="Process asset disposal transaction"
                variant="action"
                onClick={handleDisposal}
                disabled={!isSingleSelection}
              />
            </div>

            {/* Other Actions */}
            <div className="space-y-2 border-t pt-3">
              <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider px-1">
                Other Actions
              </h4>
              <ActionCard
                icon={Printer}
                title="Print Asset Labels"
                description={`Generate labels for ${selectedRowIds.size} asset${selectedRowIds.size !== 1 ? 's' : ''}`}
                variant="action"
                onClick={handlePrintLabels}
              />
              <ActionCard
                icon={Trash2}
                title="Delete Assets"
                description={`Permanently remove ${selectedRowIds.size} asset${selectedRowIds.size !== 1 ? 's' : ''}`}
                variant="delete"
                onClick={handleDelete}
              />
            </div>
          </>
        )}
      </div>
    );
  };

  // Calculate visible columns count for badge
  const visibleColumnsForBadge = contextVisibleColumns.length > 0 
    ? contextVisibleColumns 
    : DEFAULT_ASSET_COLUMNS.filter(col => col.visible).map(col => col.id);

  // Define page-specific tabs for the right panel
  const rightPanelTabs: RightPanelTab[] = [
    {
      id: 'actions',
      label: 'Actions',
      icon: Zap,
      content: <CustomActionsTab />,
      showBadge: true,
    },
    {
      id: 'filters',
      label: 'Filters',
      icon: Filter,
      content: <AssetFiltersTab />,
      badgeCount: activeFilterCount,
    },
    {
      id: 'columns',
      label: 'Columns',
      icon: Columns,
      content: <AssetColumnsTab viewMode={viewMode} onViewModeChange={setViewMode} />,
      badgeCount: visibleColumnsForBadge.length,
    },
  ];

  return (
    <PageWithRightPanel>
      <div className="flex h-full w-full flex-col overflow-hidden">
        {/* Page Header */}
        <div className="border-b bg-card px-6 py-3 flex-shrink-0" data-tour="assets-header">
          <div className="flex items-center justify-between gap-4">
            {/* Left side - Search, Sort, View Controls */}
            <div className="flex items-center gap-3">
              <div className="relative w-80" data-tour="search-bar">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search assets..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                  data-tour="search-input"
                />
              </div>

              {/* Vertical Divider */}
              <div className="h-8 w-px bg-border" />

              {/* Sorting */}
              <div data-tour="sort-dropdown">
                <Select value={sortBy} onValueChange={(value: string) => setSortBy(value as SortOption)}>
                  <SelectTrigger className="w-[180px]">
                    <ArrowUpDown className="mr-2 h-4 w-4" />
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="category-asc">Category (A-Z)</SelectItem>
                    <SelectItem value="category-desc">Category (Z-A)</SelectItem>
                    <SelectItem value="name-asc">Name (A-Z)</SelectItem>
                    <SelectItem value="name-desc">Name (Z-A)</SelectItem>
                    <SelectItem value="date-desc">Date (Newest)</SelectItem>
                    <SelectItem value="date-asc">Date (Oldest)</SelectItem>
                    <SelectItem value="value-desc">Value (High-Low)</SelectItem>
                    <SelectItem value="value-asc">Value (Low-High)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Vertical Divider */}
              <div className="h-8 w-px bg-border" />

              {/* View Mode Toggle */}
              <div data-tour="view-toggle">
                <ToggleGroup
                  type="single"
                  value={viewMode}
                  onValueChange={(value) => value && setViewMode(value as 'gallery' | 'list')}
                >
                  <ToggleGroupItem value="list" aria-label="List view">
                    <List className="h-4 w-4" />
                  </ToggleGroupItem>
                  <ToggleGroupItem value="gallery" aria-label="Gallery view">
                    <LayoutGrid className="h-4 w-4" />
                  </ToggleGroupItem>
                </ToggleGroup>
              </div>

              {/* Grid Size Selector */}
              {viewMode === 'gallery' && (
                <div data-tour="grid-size">
                  <Select value={gridSize.toString()} onValueChange={(value: string) => setGridSize(Number(value) as GridSize)}>
                    <SelectTrigger className="w-[140px]">
                      <Grid3x3 className="mr-2 h-4 w-4" />
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="4">4 Columns</SelectItem>
                      <SelectItem value="5">5 Columns</SelectItem>
                      <SelectItem value="6">6 Columns</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            
            {/* Right side - Action Buttons */}
            <div className="flex items-center gap-2">
              <Button onClick={handleNewAsset} data-tour="new-asset-button">
                <Plus className="mr-2 h-4 w-4" />
                New Asset
              </Button>
            </div>
          </div>
        </div>

        {/* Active Filter Badges */}
        {(searchParams.get('filter') === 'requiring-attention' || importJobInfo) && (
          <div className="border-b bg-muted/50 px-6 py-2 flex-shrink-0">
            {searchParams.get('filter') === 'requiring-attention' && (
              <Badge variant="secondary" className="gap-2 mr-2">
                <span>Assets Requiring Attention: Warranty, Insurance, or Maintenance expiring within 30 days, or Disposal status</span>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-4 w-4 p-0 hover:bg-transparent"
                  onClick={() => window.location.href = '/assets'}
                >
                  <X className="h-3 w-3" />
                </Button>
              </Badge>
            )}
            {importJobInfo && (
              <Badge variant="secondary" className="gap-2">
                <span>Showing assets from import: {importJobInfo.fileName}</span>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-4 w-4 p-0 hover:bg-transparent"
                  onClick={() => window.location.href = '/assets'}
                >
                  <X className="h-3 w-3" />
                </Button>
              </Badge>
            )}
          </div>
        )}

        {/* Content Container */}
        <div className="flex-1 overflow-hidden flex flex-col min-h-0">
          {(() => {
            if (initialLoad || loading || isApplyingFilters) {
              return (
                <div className="px-6 py-4">
                  {viewMode === 'gallery' ? (
                    <GallerySkeleton items={12} />
                  ) : (
                    <TableSkeleton rows={10} />
                  )}
                </div>
              );
            }
            if (error && mode === 'live') {
              return (
                <LiveModePlaceholder
                  variant="error"
                  title="Failed to load assets"
                  description={error}
                  onRetry={() => window.location.reload()}
                  isLoading={loading}
                />
              );
            }
            if (error) {
              return (
                <div className="flex h-full items-center justify-center">
                  <div className="text-center">
                    <p className="text-lg font-medium text-destructive">Error</p>
                    <p className="text-sm text-muted-foreground">{error}</p>
                  </div>
                </div>
              );
            }
            if (mode === 'live' && assets.length === 0 && !searchQuery && (!tableFilters.status || tableFilters.status.length === 0) && (!tableFilters.category || tableFilters.category.length === 0)) {
              return (
                <LiveModePlaceholder
                  variant="empty"
                  title="No assets found"
                  description="The API returned no assets. This could mean the database is empty or there's a connection issue."
                  onRetry={() => window.location.reload()}
                  isLoading={loading}
                />
              );
            }
            if (viewMode === 'gallery') {
              return (
            <div className="flex-1 overflow-auto">
              <div className="px-6 py-4">
                {/* Select All for Gallery View */}
                <div className="mb-4 flex items-center gap-3" data-tour="asset-selection">
                  <Checkbox
                    checked={paginatedAssets.length > 0 && paginatedAssets.every(asset => selectedRowIds.has(asset.id))}
                    onCheckedChange={handleSelectAll}
                    aria-label="Select all assets"
                  />
                  <span className="text-sm font-medium">
                    {selectedRowIds.size > 0 
                      ? `${selectedRowIds.size} item${selectedRowIds.size !== 1 ? 's' : ''} selected`
                      : `${filteredAssets.length} asset${filteredAssets.length !== 1 ? 's' : ''}`
                    }
                  </span>
                </div>

                {filteredAssets.length === 0 ? (
                  <div className="flex h-full items-center justify-center py-12">
                    <div className="text-center space-y-4">
                      <div className="flex justify-center">
                        <div className="rounded-full bg-slate-100 dark:bg-slate-800 p-6">
                          <PackageOpen className="h-12 w-12 text-slate-400 dark:text-slate-500" />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">No assets found</h3>
                        <p className="text-sm text-muted-foreground max-w-sm mx-auto">
                          {searchQuery || (tableFilters.status && tableFilters.status.length > 0) || (tableFilters.category && tableFilters.category.length > 0)
                            ? "Try adjusting your search or filters to find what you're looking for"
                            : "Get started by creating your first asset"}
                        </p>
                      </div>
                      {!searchQuery && (!tableFilters.status || tableFilters.status.length === 0) && (!tableFilters.category || tableFilters.category.length === 0) && (
                        <Button onClick={handleNewAsset} className="mt-4">
                          <Plus className="mr-2 h-4 w-4" />
                          New Asset
                        </Button>
                      )}
                    </div>
                  </div>
                ) : (
                  <AssetGallery
                    assets={paginatedAssets}
                    viewMode={viewMode}
                    gridSize={gridSize}
                    onAssetClick={handleAssetClick}
                    selectedAssetIds={selectedRowIds}
                    onSelectAsset={handleSelectionChange}
                  />
                )}
              </div>
            </div>
              );
            }
            return (
            <div className="flex-1 overflow-auto">
              <Table>
                  <TableHeader className="sticky top-0 z-10 bg-slate-50 dark:bg-slate-900 border-b-2 border-slate-200 dark:border-slate-700">
                  <TableRow className="hover:bg-transparent">
                    {/* Checkbox column */}
                    <TableHead className="font-semibold relative" style={{ width: `${columnWidths.select}px` }}>
                      <div className="flex items-center justify-center px-3">
                        <Checkbox
                          checked={paginatedAssets.length > 0 && paginatedAssets.every(asset => selectedRowIds.has(asset.id))}
                          onCheckedChange={handleSelectAll}
                        />
                      </div>
                      {/* Column divider - not resizable for checkbox */}
                      <div className="absolute right-0 top-1/2 -translate-y-1/2 h-6">
                        <div className="w-px h-full bg-slate-300 dark:bg-slate-600" />
                      </div>
                    </TableHead>
                    {orderedVisibleColumns.map((column) => {
                      const sortKey = getSortKeyForColumn(column.id);
                      const isRightAligned = ['acquisitionCost', 'netBookValue', 'accumulatedDepreciation', 'quantity'].includes(column.id);

                      return (
                        <TableHead 
                          key={column.id} 
                          className={cn("font-semibold text-slate-700 dark:text-slate-200 relative", isRightAligned ? 'text-right' : '')}
                          style={{ width: `${columnWidths[column.id]}px` }}
                        >
                          {sortKey ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="-ml-3 h-8 hover:bg-slate-200 dark:hover:bg-slate-800 font-semibold"
                              onClick={() => {
                                // Cycle through: unsorted → asc → desc → unsorted
                                if (column.id === 'name') {
                                  setSortBy(getNextSortState(sortBy, 'name', 'name-asc', 'name-desc'));
                                } else if (column.id === 'category') {
                                  setSortBy(getNextSortState(sortBy, 'category', 'category-asc', 'category-desc'));
                                } else if (column.id === 'acquisitionDate') {
                                  setSortBy(getNextSortState(sortBy, 'date', 'date-asc', 'date-desc'));
                                } else if (column.id === 'netBookValue') {
                                  setSortBy(getNextSortState(sortBy, 'value', 'value-asc', 'value-desc'));
                                }
                              }}
                            >
                              {column.label}
                              <SortIcon columnKey={sortKey} />
                            </Button>
                          ) : (
                            column.label
                          )}
                          {/* Resizable column divider */}
                          <div
                            className="absolute right-0 top-1/2 -translate-y-1/2 h-6 w-1 cursor-col-resize hover:bg-primary/20 group"
                            onMouseDown={(e) => handleMouseDown(column.id, e.clientX)}
                          >
                            <div className="absolute right-0 top-0 bottom-0 w-px bg-slate-300 dark:bg-slate-600 group-hover:bg-primary" />
                          </div>
                        </TableHead>
                      );
                    })}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedAssets.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={orderedVisibleColumns.length + 1}
                        className="h-96"
                      >
                        <div className="flex flex-col items-center justify-center space-y-4 py-12">
                          <div className="rounded-full bg-slate-100 dark:bg-slate-800 p-6">
                            <PackageOpen className="h-12 w-12 text-slate-400 dark:text-slate-500" />
                          </div>
                          <div className="space-y-2 text-center">
                            <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">No assets found</h3>
                            <p className="text-sm text-muted-foreground max-w-sm mx-auto">
                              {searchQuery || (tableFilters.status && tableFilters.status.length > 0) || (tableFilters.category && tableFilters.category.length > 0)
                                ? "Try adjusting your search or filters to find what you're looking for"
                                : "Get started by creating your first asset"}
                            </p>
                          </div>
                          {!searchQuery && (!tableFilters.status || tableFilters.status.length === 0) && (!tableFilters.category || tableFilters.category.length === 0) && (
                            <Button onClick={handleNewAsset} className="mt-4">
                              <Plus className="mr-2 h-4 w-4" />
                              New Asset
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedAssets.map((asset, index) => (
                      <TableRow 
                        key={asset.id}
                        className={cn(
                          "transition-colors border-b border-slate-100 dark:border-slate-800",
                          index % 2 === 0 
                            ? "bg-white dark:bg-slate-950 hover:bg-slate-50 dark:hover:bg-slate-900" 
                            : "bg-slate-50/50 dark:bg-slate-900/50 hover:bg-slate-100 dark:hover:bg-slate-800"
                        )}
                      >
                        {/* Checkbox cell */}
                        <TableCell style={{ width: `${columnWidths.select}px` }}>
                          <div className="flex items-center justify-center">
                            <Checkbox
                              checked={selectedRowIds.has(asset.id)}
                              onCheckedChange={(checked) => handleSelectionChange(asset.id, checked as boolean)}
                            />
                          </div>
                        </TableCell>
                        {orderedVisibleColumns.map((column) => renderCellContent(column.id, asset))}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
            );
          })()}
        </div>

        {/* Footer - Sticky at bottom */}
        {!loading && !error && filteredAssets.length > 0 && (
          <div className="border-t bg-card px-6 py-3 flex-shrink-0">
            <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-3 text-sm text-muted-foreground">
              <div className="flex-shrink-0">
                Showing {startIndex + 1}-{Math.min(endIndex, filteredAssets.length)} of {filteredAssets.length} entries
              </div>
              
              {/* Pagination Controls */}
              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 flex-shrink-0">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1}
                  >
                    First
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Previous
                  </Button>
                  
                  {/* Page Number Buttons */}
                  <div className="flex items-center gap-1">
                    {(() => {
                      const pageNumbers: (number | string)[] = [];
                      const maxVisiblePages = 7;
                      
                      if (totalPages <= maxVisiblePages) {
                        for (let i = 1; i <= totalPages; i++) {
                          pageNumbers.push(i);
                        }
                      } else {
                        pageNumbers.push(1);
                        
                        if (currentPage > 3) {
                          pageNumbers.push('...');
                        }
                        
                        const start = Math.max(2, currentPage - 1);
                        const end = Math.min(totalPages - 1, currentPage + 1);
                        
                        for (let i = start; i <= end; i++) {
                          pageNumbers.push(i);
                        }
                        
                        if (currentPage < totalPages - 2) {
                          pageNumbers.push('...');
                        }
                        
                        pageNumbers.push(totalPages);
                      }
                      
                      return pageNumbers.map((page, pageIndex) => {
                        if (page === '...') {
                          return (
                            <span key={`ellipsis-${pageIndex}`} className="px-2 text-muted-foreground">
                              ...
                            </span>
                          );
                        }
                        
                        return (
                          <Button
                            key={`page-${page}`}
                            variant={currentPage === page ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => setCurrentPage(page as number)}
                            className="min-w-[36px]"
                          >
                            {page}
                          </Button>
                        );
                      });
                    })()}
                  </div>
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    Next
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={currentPage === totalPages}
                  >
                    Last
                  </Button>
                </div>
              )}
              
              <div className="flex-shrink-0">
                Total NBV: {formatCurrency(
                  filteredAssets.reduce((sum, asset) => sum + asset.netBookValue, 0),
                  filteredAssets[0]?.currency || 'GBP'
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete {selectedRowIds.size} asset(s). This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Disposal Wizard */}
      {disposalAsset && (
        <DisposalWizard
          asset={disposalAsset}
          open={!!disposalAsset}
          onClose={() => setDisposalAsset(null)}
          onComplete={handleDisposalComplete}
        />
      )}

      {/* Label Printing Modal */}
      <LabelPrintingModal
        open={showLabelPrintingModal}
        onClose={() => setShowLabelPrintingModal(false)}
        assets={selectedAssets}
      />

      {/* Page-specific Right Panel */}
      <div data-tour="right-panel">
        <RightPanelBase tabs={rightPanelTabs} defaultTab="actions" />
      </div>

      {/* Product Tour */}
      <ProductTour
        steps={tourSteps}
        isOpen={isTourOpen}
        onClose={() => {
          setIsExpanded(false);
          closeTour();
        }}
        onComplete={() => {
          setIsExpanded(false);
          completeTour();
        }}
        onStepChange={(stepIndex) => {
          // Step 8 (index 7) is the "Actions & Filters Panel" step
          if (stepIndex === 7) {
            setIsExpanded(true);
            setActiveTab('actions');
          } else {
            setIsExpanded(false);
          }
        }}
      />
    </PageWithRightPanel>
  );
}

export default AssetsPage;
